import { create } from 'zustand';
import type { 
  Material, 
  CustomizationOptions, 
  SuggestedProduct,
  Step,
  FormState,
  SelectedMaterial
} from '../types/customization';

interface CustomizationState {
  currentStep: Step;
  visitedSteps: Set<Step>;
  selectedMaterial: Material | null;
  customizationPath: 'slab-only' | 'custom' | null;
  customizationOptions: CustomizationOptions | null;
  materialFilters: Record<string, any>;
  selectedMaterials: SelectedMaterial[];
  formState: FormState | null;
  additionalProducts: SuggestedProduct[];
  setStep: (step: Step) => void;
  setMaterial: (material: Material) => void;
  setPath: (path: 'slab-only' | 'custom') => void;
  setCustomizationOptions: (options: CustomizationOptions) => void;
  setMaterialFilters: (filters: Record<string, any>) => void;
  clearMaterialFilters: () => void;
  setSelectedMaterials: (materials: SelectedMaterial[]) => void;
  updateMaterialFinish: (materialId: string, finish: 'glossy' | 'matte') => void;
  setFormState: (state: FormState) => void;
  addAdditionalProduct: (product: SuggestedProduct) => void;
  removeAdditionalProduct: (productType: string) => void;
  canProceedToStep: (step: Step) => boolean;
}

const stepRequirements: Record<Step, (state: CustomizationState) => boolean> = {
  'cinematic': () => true,
  'material-selection': () => true,
  'purchase-path': (state) => !!state.selectedMaterial,
  'customization-type': (state) => !!state.customizationPath && state.customizationPath === 'custom',
  'customize': (state) => !!state.customizationOptions?.type || state.currentStep === 'customization-type',
  'inquiry': (state) => 
    (state.customizationPath === 'slab-only' && state.selectedMaterials.length > 0) || 
    (state.customizationPath === 'custom' && !!state.customizationOptions),
  'confirmation': (state) => !!state.formState,
  'material-comparison': (state) => state.selectedMaterials.length >= 2
};

const defaultCustomizationOptions: Partial<CustomizationOptions> = {
  thickness: 12,
  edgeProfile: 'straight'
};

const initialState: Partial<CustomizationState> = {
  currentStep: 'cinematic',
  visitedSteps: new Set(['cinematic']),
  selectedMaterial: null,
  customizationPath: null,
  customizationOptions: null,
  materialFilters: {},
  selectedMaterials: [],
  formState: null,
  additionalProducts: []
};

export const useCustomizationStore = create<CustomizationState>((set, get) => ({
  ...initialState as CustomizationState,
  
  setStep: (step) => {
    const state = get();
    const canProceed = state.canProceedToStep(step);
    if (canProceed) {
      const visitedSteps = new Set(state.visitedSteps);
      visitedSteps.add(step);
      set({ currentStep: step, visitedSteps });
    }
  },
  
  setMaterial: (material) => set({ selectedMaterial: material }),
  
  setPath: (path) => set({ customizationPath: path }),
  
  setCustomizationOptions: (options) => {
    const state = get();
    const updatedOptions = {
      ...defaultCustomizationOptions,
      ...options
    };
    set({ customizationOptions: updatedOptions });
    if (state.currentStep === 'customization-type') {
      state.setStep('customize');
    }
  },
  
  setMaterialFilters: (filters) => set((state) => ({
    materialFilters: { ...state.materialFilters, ...filters }
  })),
  
  clearMaterialFilters: () => set({ materialFilters: {} }),
  
  setSelectedMaterials: (materials) => set({ selectedMaterials: materials }),

  updateMaterialFinish: (materialId, finish) => set(state => ({
    selectedMaterials: state.selectedMaterials.map(item => 
      item.material.id === materialId 
        ? { ...item, material: { ...item.material, selectedFinish: finish } }
        : item
    )
  })),
  
  setFormState: (state) => set({ formState: state }),
  
  addAdditionalProduct: (product) => set((state) => ({
    additionalProducts: [...state.additionalProducts, product]
  })),
  
  removeAdditionalProduct: (productType) => set((state) => ({
    additionalProducts: state.additionalProducts.filter(p => p.type !== productType)
  })),

  canProceedToStep: (step) => {
    const state = get();
    const requirement = stepRequirements[step];
    
    if (step === 'customize' && state.currentStep === 'customization-type') {
      return true;
    }
    
    if (step === 'inquiry' && state.currentStep === 'purchase-path') {
      return true;
    }

    return requirement ? requirement(state) : false;
  }
}));